import { createContext, useContext } from 'react';
import { useUsage } from '@/contexts/UsageContext';

export type AdProviderType = 'adsense' | 'admob' | null;
export type AdState = 'idle' | 'countdown' | 'watching' | 'completed' | 'error';
export type AdAction = 'video_generate' | 'extra_scan' | 'ai_chat_bonus' | 'download_unlock' | 'hd_export';
export interface AdReward { action: AdAction; label: string; grant: string }

interface AdContextValue {
  isPremium: boolean;
  adProvider: AdProviderType;
  adState: AdState;
  showAdGate: boolean;
  currentReward: AdReward | null;
  countdownSeconds: number;
  watchAd: (action: AdAction, label: string, grant: string, onComplete: () => void, onCancel?: () => void) => void;
  completeAd: () => void;
  skipAd: () => void;
  setPremiumOverride: (premium: boolean) => void;
  setAdProvider: (provider: AdProviderType) => void;
  initAdProvider: () => Promise<void>;
  loadRewardedAd: (unitId: string) => Promise<boolean>;
}

const AdContext = createContext<AdContextValue | null>(null);

export function AdProvider({ children }: { children: React.ReactNode }) {
  const { tier, refreshUsage } = useUsage();
  // There is no advertising integration. Never simulate impressions, wait for
  // a fake countdown, or grant rewards/access from a client-side timer.
  // Preserve the existing consumer API without pretending an SDK is connected.
  return <AdContext.Provider value={{
    isPremium: tier.tier === 'premium',
    adProvider: null,
    adState: 'idle',
    showAdGate: false,
    currentReward: null,
    countdownSeconds: 0,
    watchAd: (_action, _label, _grant, _onComplete, onCancel) => onCancel?.(),
    completeAd: () => {},
    skipAd: () => {},
    setPremiumOverride: () => { void refreshUsage(); },
    setAdProvider: () => {},
    initAdProvider: async () => {},
    loadRewardedAd: async () => false,
  }}>{children}</AdContext.Provider>;
}

export function useAd() {
  const context = useContext(AdContext);
  if (!context) throw new Error('useAd must be used within AdProvider');
  return context;
}
